/**
 * The specific reasons a GPX (or KML/TCX/GeoJSON) import can fail. Every user-facing
 * import flow classifies its failure into one of these so we can tell the user exactly
 * why, instead of a single generic "not a valid file" message.
 *
 * This module is intentionally free of i18n/vue imports so it stays trivially unit-testable
 * and cheap to import from parsing helpers. Message resolution lives in
 * `gpxImportErrorMessage.ts`.
 */
export enum GpxImportErrorCode {
  Unreadable = "unreadable",
  Empty = "empty",
  NotGpx = "notGpx",
  UnsupportedType = "unsupportedType",
  MalformedXml = "malformedXml",
  NoRouteOrTrack = "noRouteOrTrack",
  /** A route/course file: points but no per-point timestamps, so it is not a recording. */
  NoTimestamps = "noTimestamps",
  NoWaypoints = "noWaypoints",
  NoCoordinates = "noCoordinates",
  MultipleRoutes = "multipleRoutes",
  Generic = "generic",
}

export class GpxImportError extends Error {
  code: GpxImportErrorCode;

  constructor(code: GpxImportErrorCode) {
    super(code);
    this.name = "GpxImportError";
    this.code = code;
    // Restore prototype chain when compiled down to ES5 (so `instanceof` works).
    Object.setPrototypeOf(this, GpxImportError.prototype);
  }
}

/**
 * DOMParser reports malformed XML via an injected <parsererror> node rather than
 * throwing, so a damaged file otherwise parses to zero features and looks "empty".
 */
export function isMalformedXml(xml: string): boolean {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  return doc.getElementsByTagName("parsererror").length > 0;
}
